import type { IdentifierRecord } from '@myplayer/schema';
import { IconAlertCircle, IconCheck, IconUpload } from '@myplayer/ui';
import {
  useState,
  type ChangeEvent,
  type Dispatch,
  type ReactElement,
  type SetStateAction,
} from 'react';
import type { AdminUserSession } from './auth-context.js';
import { Badge, Button, ModalDialog, ToastNotification } from './components/ui/primitives.js';
import {
  exportIdentifiersToCsv,
  parseAndValidateIdentifiersCsv,
  stageValidatedCsvRowsAsDrafts,
  type CsvImportValidationReport,
} from './products-csv.js';
import { triggerBrowserDownload } from './qr-generator.js';
import {
  resolveEffectiveHostname,
  resolveExampleBrandHostname,
} from './use-workspace-identifiers.js';

export function useProductsCsvController(params: {
  readonly session: AdminUserSession | null;
  readonly effectiveResolverHost: string;
  readonly records: readonly IdentifierRecord[];
  readonly allOrgRecords?: readonly IdentifierRecord[];
  readonly knownWorkspaceIds?: readonly string[];
  readonly workspaceNameMap?: ReadonlyMap<string, string>;
  readonly filteredRecords: readonly IdentifierRecord[];
  readonly isControlled: boolean;
  readonly setRecords: Dispatch<SetStateAction<readonly IdentifierRecord[]>>;
  readonly setSelectedKey: (key: string) => void;
}) {
  const [showCsvImportPanel, setShowCsvImportPanel] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [csvReport, setCsvReport] = useState<CsvImportValidationReport | null>(null);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvImportError, setCsvImportError] = useState<string | null>(null);
  const [csvImportSuccess, setCsvImportSuccess] = useState<string | null>(null);
  const [lastExportedCsv, setLastExportedCsv] = useState<string | null>(null);

  const handleValidateCsv = (): void => {
    setCsvImportError(null);
    setCsvImportSuccess(null);
    const resolverHostname = resolveEffectiveHostname(params.effectiveResolverHost);
    const report = parseAndValidateIdentifiersCsv(csvText, {
      ...(params.session?.uid !== undefined ? { submittedBy: params.session.uid } : {}),
      ...(resolverHostname.length > 0 ? { resolverHosts: [resolverHostname] } : {}),
      existingRecords: params.allOrgRecords ?? params.records,
      ...(params.session?.orgId !== undefined ? { orgId: params.session.orgId } : {}),
      ...(params.session?.activeWorkspaceId !== undefined
        ? { workspaceId: params.session.activeWorkspaceId }
        : {}),
      ...(params.workspaceNameMap !== undefined
        ? { workspaceNameMap: params.workspaceNameMap }
        : {}),
    });
    setCsvReport(report);
  };

  const handleStageValidCsvRows = async (): Promise<void> => {
    if (!params.session || !csvReport || csvReport.validRows.length === 0) return;
    setCsvImportError(null);
    setCsvImportSuccess(null);
    setCsvImporting(true);
    try {
      const stagedRecords = await stageValidatedCsvRowsAsDrafts({
        orgId: params.session.orgId,
        workspaceId: params.session.activeWorkspaceId,
        uid: params.session.uid,
        validRows: csvReport.validRows,
        existingRecords: params.allOrgRecords ?? params.records,
        isControlled: params.isControlled,
        ...(params.knownWorkspaceIds !== undefined
          ? { knownWorkspaceIds: params.knownWorkspaceIds }
          : {}),
        ...(params.workspaceNameMap !== undefined
          ? { workspaceNameMap: params.workspaceNameMap }
          : {}),
      });
      if (params.isControlled) {
        params.setRecords((prev) => {
          const map = new Map(prev.map((r) => [r.identifierKey, r]));
          for (const staged of stagedRecords) {
            map.set(staged.identifierKey, staged);
          }
          return Array.from(map.values());
        });
      }
      setCsvImportSuccess(
        `Staged ${stagedRecords.length} identifier${stagedRecords.length === 1 ? '' : 's'} as drafts (pending promotion validation).`,
      );
    } catch (err: unknown) {
      setCsvImportError(err instanceof Error ? err.message : 'Failed to stage CSV drafts');
    } finally {
      setCsvImporting(false);
    }
  };

  const handleExportCsv = (): void => {
    const csvContent = exportIdentifiersToCsv(params.filteredRecords, params.effectiveResolverHost);
    setLastExportedCsv(csvContent);
    const wsId = params.session?.activeWorkspaceId ?? 'workspace';
    triggerBrowserDownload(`identifiers-${wsId}.csv`, csvContent, 'text/csv;charset=utf-8');
  };

  return {
    showCsvImportPanel,
    setShowCsvImportPanel,
    csvText,
    setCsvText,
    csvReport,
    setCsvReport,
    csvImporting,
    csvImportError,
    setCsvImportError,
    csvImportSuccess,
    setCsvImportSuccess,
    lastExportedCsv,
    handleValidateCsv,
    handleStageValidCsvRows,
    handleExportCsv,
  };
}

export interface ProductsCsvPanelProps {
  readonly csvText: string;
  readonly setCsvText: (value: string) => void;
  readonly csvReport: CsvImportValidationReport | null;
  readonly csvImporting: boolean;
  readonly csvImportError: string | null;
  readonly csvImportSuccess: string | null;
  readonly onValidateCsv: () => void;
  readonly onStageValidRows: () => Promise<void>;
  readonly onClose: () => void;
}

export function ProductsCsvPanel(props: ProductsCsvPanelProps): ReactElement {
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        props.setCsvText(reader.result);
      }
    };
    reader.readAsText(file);
  };

  return (
    <ModalDialog
      open={true}
      title="Import GS1 Identifiers from CSV"
      subtitle="Every row is checked against GS1 syntax, check-digit rules, and destination URL safety. Valid rows are saved strictly as staged drafts."
      icon={<IconUpload />}
      maxWidthClassName="max-w-3xl"
      submitting={props.csvImporting}
      onClose={props.onClose}
      data-testid="csv-import-panel"
      footer={
        <Button type="button" size="sm" variant="secondary" onClick={props.onClose}>
          <span>Close</span>
        </Button>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col">
            <span className="mb-1 block text-[12px] font-medium text-fg-secondary">
              Upload CSV File
            </span>
            <label
              htmlFor="csv-file-upload"
              className="group flex flex-1 cursor-pointer flex-col items-center justify-center gap-2 rounded-control border border-dashed border-border bg-surface-subtle p-4 text-center transition-colors hover:border-border-medium hover:bg-surface"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full border border-subtle bg-surface text-fg-secondary transition-colors group-hover:border-border-medium">
                <IconUpload />
              </span>
              <span className="text-[13px] font-medium text-fg">
                Click to select a <span className="font-mono">.csv</span> file
              </span>
              <span className="text-[11px] text-fg-muted">
                UTF-8 CSV with header row (ai, primaryKey, title, brand, sector, defaultUrl)
              </span>
              <input
                id="csv-file-upload"
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileChange}
                className="sr-only"
              />
            </label>
          </div>
          <div>
            <label
              htmlFor="csv-text-input"
              className="mb-1 block text-[12px] font-medium text-fg-secondary"
            >
              Or Paste CSV Rows
            </label>
            <textarea
              id="csv-text-input"
              aria-label="CSV Content"
              rows={5}
              value={props.csvText}
              onChange={(e) => props.setCsvText(e.target.value)}
              placeholder={`ai,primaryKey,title,brand,sector,defaultUrl\n01,09506000134352,Organic Oat Milk 1L,MyPlayer Foods,RETAIL_CPG,https://${resolveExampleBrandHostname()}/products/oat-milk`}
              className="w-full rounded-control border border-border bg-surface-subtle p-2.5 font-mono text-[12px] text-fg transition-colors focus:border-brand focus:bg-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-wash"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={props.onValidateCsv}
            disabled={props.csvText.trim().length === 0 || props.csvImporting}
          >
            <IconUpload />
            <span>Validate CSV Rows</span>
          </Button>
          {props.csvReport !== null && props.csvReport.validRows.length > 0 && (
            <Button
              type="button"
              variant="primary"
              data-testid="stage-valid-csv-rows-btn"
              onClick={() => void props.onStageValidRows()}
              disabled={props.csvImporting}
            >
              <IconCheck />
              <span>
                {props.csvImporting
                  ? 'Staging Drafts…'
                  : `Stage ${props.csvReport.validRows.length} Valid Row${props.csvReport.validRows.length === 1 ? '' : 's'} as Drafts`}
              </span>
            </Button>
          )}
        </div>

        {props.csvImportError !== null && (
          <div
            role="alert"
            className="rounded-control border border-status-error-border bg-status-error-bg p-3 text-[12px] text-status-error"
          >
            {props.csvImportError}
          </div>
        )}

        <ToastNotification
          data-testid="csv-import-success-banner"
          message={props.csvImportSuccess}
        />

        {props.csvReport !== null && (
          <div data-testid="csv-validation-report" className="space-y-4">
            <div className="flex flex-wrap items-center gap-3 text-[13px]">
              <Badge variant="default" data-testid="csv-total-count">
                Total rows: {props.csvReport.totalRows}
              </Badge>
              <Badge variant="valid" data-testid="csv-valid-count">
                <IconCheck /> {props.csvReport.validRows.length} valid (ready to stage as draft)
              </Badge>
              <Badge
                variant={props.csvReport.invalidRows.length > 0 ? 'invalid' : 'default'}
                data-testid="csv-invalid-count"
              >
                <IconAlertCircle /> {props.csvReport.invalidRows.length} rejected
              </Badge>
            </div>

            {props.csvReport.validRows.length > 0 && (
              <div className="overflow-x-auto rounded-card border border-border">
                <table
                  aria-label="Valid CSV rows ready to stage"
                  data-testid="csv-valid-rows-table"
                  className="w-full border-collapse text-left text-[13px]"
                >
                  <thead>
                    <tr className="border-b border-border bg-surface-subtle text-[12px] font-medium text-fg-secondary">
                      <th className="px-3 py-2">Row</th>
                      <th className="px-3 py-2">Identifier</th>
                      <th className="px-3 py-2">Title</th>
                      <th className="px-3 py-2">Sector</th>
                      <th className="px-3 py-2">Default Destination</th>
                      <th className="px-3 py-2">Target State</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-subtle">
                    {props.csvReport.validRows.map((row) => (
                      <tr key={`${row.rowNumber}-${row.identifierKey}`}>
                        <td className="px-3 py-2 font-mono text-[12px] text-fg-secondary tabular-nums">
                          #{row.rowNumber}
                        </td>
                        <td className="px-3 py-2 font-mono text-[12px] font-semibold text-fg tabular-nums">
                          /{row.ai}/{row.primaryKey}
                        </td>
                        <td className="px-3 py-2 text-fg">{row.title}</td>
                        <td className="px-3 py-2 text-[12px] text-fg-secondary">{row.sector}</td>
                        <td className="px-3 py-2 font-mono text-[12px] text-fg-secondary">
                          {row.defaultUrl}
                        </td>
                        <td className="px-3 py-2">
                          <Badge variant="default">Staged Draft</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {props.csvReport.invalidRows.length > 0 && (
              <div className="overflow-x-auto rounded-card border border-status-error-border">
                <table
                  aria-label="Rejected CSV rows with reasons"
                  data-testid="csv-invalid-rows-table"
                  className="w-full border-collapse text-left text-[13px]"
                >
                  <thead>
                    <tr className="border-b border-status-error-border bg-status-error-bg text-[12px] font-medium text-status-error">
                      <th className="px-3 py-2">Row</th>
                      <th className="px-3 py-2">AI / Key</th>
                      <th className="px-3 py-2">Title</th>
                      <th className="px-3 py-2">Reasons Rejected</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-subtle">
                    {props.csvReport.invalidRows.map((bad) => (
                      <tr key={`bad-${bad.rowNumber}`}>
                        <td className="px-3 py-2 font-mono text-[12px] text-fg-secondary tabular-nums">
                          #{bad.rowNumber}
                        </td>
                        <td className="px-3 py-2 font-mono text-[12px] text-fg tabular-nums">
                          /{bad.rawAi}/{bad.rawPrimaryKey || '—'}
                        </td>
                        <td className="px-3 py-2 text-fg">{bad.rawTitle || '—'}</td>
                        <td className="px-3 py-2 text-[12px] text-status-error">
                          <ul className="list-inside list-disc space-y-0.5">
                            {bad.reasons.map((reason, idx) => (
                              <li key={idx}>{reason}</li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </ModalDialog>
  );
}
